import type { ReactNode } from 'react';
import { StatusLamp } from './StatusLamp.tsx';
import type { LampTone } from './StatusLamp.tsx';
import { cx } from './cx.ts';

/**
 * KPI stat card: label row (optionally lamp-lit), mono `stat` value,
 * mono delta line. `tone` recolors the value for alert states; the
 * card frame warms to `bad-line` when tone is `bad`.
 */
export type KpiTone = 'default' | 'ok' | 'warn' | 'bad';

const VALUE_TONE: Record<KpiTone, string> = {
  default: 'text-paper',
  ok: 'text-ok',
  warn: 'text-warn',
  bad: 'text-bad',
};

export type KpiDeltaDirection = 'up' | 'down' | 'flat';

const DELTA_TONE: Record<'good' | 'bad' | 'neutral', string> = {
  good: 'text-ok',
  bad: 'text-bad',
  neutral: 'text-paper-low',
};

interface Props {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly unit?: ReactNode;
  readonly delta?: {
    readonly text: ReactNode;
    readonly tone: 'good' | 'bad' | 'neutral';
  };
  readonly tone?: KpiTone;
  readonly lamp?: LampTone;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function KpiCard({ label, value, unit, delta, tone = 'default', lamp, pulse = false, className, testId }: Props): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={cx(
        'rounded-lg border bg-card p-4',
        tone === 'bad' ? 'border-bad-line bg-bad-dim/20' : 'border-line-soft',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-label text-paper-low">
        {lamp !== undefined ? <StatusLamp tone={lamp} size="sm" pulse={pulse} /> : null}
        {label}
      </div>
      <div className={cx('mt-2 min-w-0 break-words font-mono text-stat max-md:text-title-lg', VALUE_TONE[tone])}>
        {value}
        {unit !== undefined && unit !== null ? (
          <span className="ml-0.5 text-body font-normal text-paper-low">{unit}</span>
        ) : null}
      </div>
      {delta !== undefined ? (
        <div className={cx('mt-1.5 font-mono text-label', DELTA_TONE[delta.tone])}>{delta.text}</div>
      ) : null}
    </div>
  );
}
